// The reason every player write carries (spec/dashboard.md section 11; spec/api-conventions.md sections 17, 19.5): asked
// once per Save All when a player key is staged, and by the commands (restore, reset, erase, the order actions) in their
// own dialogs through `reasonError` / `REASON_HINT`.
import { useEffect, useState, type ReactNode } from 'react';
import { Button } from './Button';
import { Field } from './Field';
import { TextArea } from './inputs';
import { Modal } from './Modal';

export const REASON_MIN = 2;
export const REASON_MAX = 2000;
export const REASON_HINT = 'Recorded in the audit log and on every version this write creates (2–2000 characters).';

/** The validation message for a reason, or null when it is acceptable. */
export function reasonError(text: string): string | null {
  const n = text.trim().length;
  if (n < REASON_MIN) return 'A reason of at least 2 characters is required.';
  if (n > REASON_MAX) return 'A reason has at most 2000 characters.';
  return null;
}

export interface ReasonDialogProps {
  open: boolean;
  title?: ReactNode;
  body?: ReactNode;
  confirmLabel?: string;
  tone?: 'primary' | 'danger';
  /** the reason, trimmed; null when cancelled */
  onResult: (reason: string | null) => void;
}

export function ReasonDialog({ open, title = 'Reason for the player edits', body, confirmLabel = 'Save', tone = 'primary', onResult }: ReasonDialogProps) {
  const [text, setText] = useState('');
  const [touched, setTouched] = useState(false);
  // every prompt starts empty: a reason belongs to one write
  useEffect(() => {
    if (open) {
      setText('');
      setTouched(false);
    }
  }, [open]);
  const error = reasonError(text);
  return (
    <Modal
      title={title}
      open={open}
      onClose={() => onResult(null)}
      describedBy="reason-body"
      footer={
        <>
          <Button variant="ghost" onClick={() => onResult(null)}>
            Cancel
          </Button>
          <Button variant={tone} disabled={!!error} onClick={() => onResult(text.trim())}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div id="reason-body">
        {body ?? <p className="muted small">{REASON_HINT}</p>}
        <Field label="Reason" required error={touched && error ? error : undefined}>
          <TextArea aria-label="Reason" value={text} rows={3} autoFocus onChange={(e) => setText(e.target.value)} onBlur={() => setTouched(true)} />
        </Field>
      </div>
    </Modal>
  );
}
